const MAX_SENTENCE_LEN = 500;

/** Plain one-line preview as shown in the list (collapsed whitespace). */
export function listItemPreviewPlain(text: string, maxLen = 50): string {
  const full = text.trim().replace(/\s+/g, ' ');
  if (full.length <= maxLen) return full;
  return `${full.slice(0, maxLen)}...`;
}

/** Character offset under (x, y) within element text, or null. */
export function textOffsetAtPoint(el: HTMLElement, clientX: number, clientY: number): number | null {
  const doc = document;
  let node: Node | null = null;
  let offset = 0;

  if (doc.caretPositionFromPoint) {
    const pos = doc.caretPositionFromPoint(clientX, clientY);
    if (!pos) return null;
    node = pos.offsetNode;
    offset = pos.offset;
  } else if (doc.caretRangeFromPoint) {
    const range = doc.caretRangeFromPoint(clientX, clientY);
    if (!range) return null;
    node = range.startContainer;
    offset = range.startOffset;
  } else {
    return null;
  }

  if (!node || !el.contains(node)) return null;
  return textOffsetInElement(el, node, offset);
}

function textOffsetInElement(root: HTMLElement, targetNode: Node, targetOffset: number): number {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let total = 0;
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (node === targetNode) return total + targetOffset;
    total += node.textContent?.length ?? 0;
  }
  return total;
}

/** Map list-preview offset → offset in full item text (handles truncation). */
export function previewOffsetToFullOffset(
  fullText: string,
  previewPlain: string,
  previewOffset: number
): number {
  const previewCore = previewPlain.endsWith('...') ? previewPlain.slice(0, -3) : previewPlain;
  const safePreview = Math.max(0, Math.min(previewOffset, previewPlain.length));
  const collapsedTarget = Math.min(safePreview, previewCore.length);

  let ci = 0;
  let ri = 0;
  const raw = fullText;
  while (ri < raw.length && ci < collapsedTarget) {
    const ch = raw[ri]!;
    if (/\s/.test(ch)) {
      while (ri < raw.length && /\s/.test(raw[ri]!)) ri++;
      if (ci < collapsedTarget) ci++;
    } else {
      ri++;
      ci++;
    }
  }
  return ri;
}

function isSentenceEndChar(ch: string): boolean {
  return ch === '.' || ch === '!' || ch === '?' || ch === '…';
}

function isWordChar(ch: string): boolean {
  if (ch.length !== 1) return false;
  return /[\p{L}\p{N}]/u.test(ch) || ch === "'" || ch === '\u2019' || ch === '-';
}

function trimWordEdges(word: string): string {
  return word.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
}

/** Word (or token) under offset in full item text. */
export function wordAtOffset(fullText: string, rawOffset: number): string {
  const text = fullText.replace(/\r\n/g, '\n');
  const clamped = Math.max(0, Math.min(rawOffset, text.length));

  let start = clamped;
  while (start > 0 && isWordChar(text[start - 1]!)) start--;
  let end = clamped;
  while (end < text.length && isWordChar(text[end]!)) end++;

  return trimWordEdges(text.slice(start, end));
}

export function wordAtItemTouch(
  fullText: string,
  previewPlain: string,
  previewOffset: number
): string {
  const rawOffset = previewOffsetToFullOffset(fullText, previewPlain, previewOffset);
  return wordAtOffset(fullText, rawOffset);
}

/** Midpoint of the current selection within an element, for sentence expansion. */
export function selectionMidOffsetInElement(el: HTMLElement): number | null {
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null;

  const range = sel.getRangeAt(0);
  const { startContainer, startOffset, endContainer, endOffset } = range;
  if (!el.contains(startContainer) || !el.contains(endContainer)) return null;

  const start = textOffsetInElement(el, startContainer, startOffset);
  const end = textOffsetInElement(el, endContainer, endOffset);
  return Math.floor((start + end) / 2);
}

/** Sentence (or line) surrounding offset in full item text. */
export function sentenceAtOffset(fullText: string, rawOffset: number): string {
  const text = fullText.replace(/\r\n/g, '\n');
  const clamped = Math.max(0, Math.min(rawOffset, text.length));

  let start = 0;
  for (let i = clamped; i > 0; i--) {
    const prev = text[i - 1]!;
    if (prev === '\n') {
      start = i;
      break;
    }
    if (isSentenceEndChar(prev)) {
      start = i;
      break;
    }
  }
  while (start < text.length && /\s/.test(text[start]!)) start++;

  let end = text.length;
  for (let i = clamped; i < text.length; i++) {
    const ch = text[i]!;
    if (ch === '\n') {
      end = i;
      break;
    }
    if (isSentenceEndChar(ch)) {
      end = i + 1;
      break;
    }
  }

  let sentence = text.slice(start, end).trim().replace(/\s+/g, ' ');
  if (sentence.length > MAX_SENTENCE_LEN) {
    sentence = sentence.slice(0, MAX_SENTENCE_LEN).trim();
  }
  return sentence;
}

export function sentenceAtItemTouch(
  fullText: string,
  previewPlain: string,
  previewOffset: number
): string {
  const rawOffset = previewOffsetToFullOffset(fullText, previewPlain, previewOffset);
  return sentenceAtOffset(fullText, rawOffset);
}
